/**
 * My Add-ons (Phase B: use / apply)
 *
 * Lists the entitlements the signed-in user owns (their AddonPurchase rows,
 * grouped by the bundle they came from). Property add-ons that aren't attached
 * to a listing yet show a "Use" action → pick one of your listings → apply.
 * Account-scoped add-ons (PERSONAL/OTHER) are active immediately, no property.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, Package, CheckCircle2, Sparkles, Gift, MapPin } from 'lucide-react';
import { createPortal } from 'react-dom';

import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/common/ToastProvider';
import SignInPrompt from '../../components/common/SignInPrompt';
import {
  fetchMyAddonPurchases,
  fetchApplicableProperties,
  applyAddonPurchase,
  type AddonPurchase,
  type ApplicableProperty,
} from '../../services/addonService';

const ACCOUNT_SCOPED_FAMILIES = ['PERSONAL', 'OTHER'];

type EntitlementState = 'account' | 'ready' | 'applied' | 'pending';

function entitlementState(p: AddonPurchase): EntitlementState {
  const family = p.addon?.family;
  const accountScoped = family ? ACCOUNT_SCOPED_FAMILIES.includes(family) : false;
  if (p.status === 'ACTIVE') {
    if (accountScoped) return 'account';
    // Mirrors the backend isReadyToApply guard (AUTO + no property + clock unstarted).
    if (!p.propertyId && !p.startDate && p.fulfillment === 'AUTO') return 'ready';
    if (p.propertyId) return 'applied';
  }
  return 'pending';
}

export default function MyAddonsPage() {
  const { t, i18n } = useTranslation('addons');
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();
  const { showToast } = useToast();
  // Brokers/companies list via the broker form; individuals list under the
  // platform FAL via the request wizard.
  const createListingHref =
    user?.accountType === 'broker' || user?.accountType === 'company'
      ? '/properties/new'
      : '/individual-property-request';

  const [purchases, setPurchases] = useState<AddonPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Property picker (apply flow)
  const [applying, setApplying] = useState<AddonPurchase | null>(null);
  const [properties, setProperties] = useState<ApplicableProperty[]>([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [applyPending, setApplyPending] = useState(false);

  const localizedName = useCallback(
    (a?: { name: string; nameAr?: string | null }) =>
      a ? (isRTL ? a.nameAr || a.name : a.name) : t('pageTitle'),
    [isRTL, t],
  );

  const propertyName = useCallback(
    (p: { title?: string | null; titleAr?: string | null }) =>
      (isRTL ? p.titleAr || p.title : p.title) || '',
    [isRTL],
  );

  const load = useCallback(async () => {
    if (!user) {
      setPurchases([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchMyAddonPurchases({ pageSize: 50 });
      setPurchases(response.data);
    } catch (err) {
      console.error('Failed to load my add-ons:', err);
      setError(t('myAddons.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openPicker = async (purchase: AddonPurchase) => {
    setApplying(purchase);
    setPropsLoading(true);
    try {
      const list = await fetchApplicableProperties();
      setProperties(list);
    } catch (err) {
      console.error('Failed to load applicable properties:', err);
      setProperties([]);
    } finally {
      setPropsLoading(false);
    }
  };

  const closePicker = () => {
    if (applyPending) return;
    setApplying(null);
    setProperties([]);
  };

  const handleApply = async (propertyId: string) => {
    if (!applying) return;
    try {
      setApplyPending(true);
      await applyAddonPurchase(applying.id, propertyId);
      showToast(t('myAddons.applySuccess'), 'success');
      setApplying(null);
      setProperties([]);
      await load();
    } catch (err) {
      console.error('Failed to apply add-on:', err);
      showToast(t('myAddons.applyError'), 'error');
    } finally {
      setApplyPending(false);
    }
  };

  // Group entitlements: bundle purchases together (by bundlePurchaseId), the rest standalone.
  const bundleGroups = new Map<string, AddonPurchase[]>();
  const standalone: AddonPurchase[] = [];
  for (const p of purchases) {
    if (p.bundlePurchaseId) {
      const arr = bundleGroups.get(p.bundlePurchaseId) || [];
      arr.push(p);
      bundleGroups.set(p.bundlePurchaseId, arr);
    } else {
      standalone.push(p);
    }
  }

  const renderRow = (purchase: AddonPurchase) => {
    const state = entitlementState(purchase);
    return (
      <div key={purchase.id} className="flex items-center justify-between gap-4 p-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-4 h-4 text-[#004D40]" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 dark:text-white truncate">
              {localizedName(purchase.addon)}
            </p>
            {state === 'applied' && purchase.property && (
              <p className="text-sm text-gray-500 truncate">
                {t('myAddons.appliedTo', { name: propertyName(purchase.property) })}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {state === 'account' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
              <Sparkles className="w-3.5 h-3.5" /> {t('myAddons.accountActive')}
            </span>
          )}
          {state === 'applied' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t('status.ACTIVE')}
            </span>
          )}
          {state === 'ready' && (
            <>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">
                {t('myAddons.readyToUse')}
              </span>
              <Button size="sm" onClick={() => openPicker(purchase)}>
                {t('myAddons.useCta')}
              </Button>
            </>
          )}
          {state === 'pending' && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap">
              {t(`status.${purchase.status}`)}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
            {t('myAddons.pageTitle')}
          </h1>
          <p className="text-gray-500 max-w-2xl">{t('myAddons.pageSubtitle')}</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {!user ? (
          <SignInPrompt message={t('signInToBuy')} />
        ) : isLoading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
          </div>
        ) : purchases.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-gray-500 mb-4">{t('myAddons.empty')}</p>
            <Link to="/addons">
              <Button variant="outline">{t('myAddons.browse')}</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Bundle groups */}
            {[...bundleGroups.entries()].map(([bundleId, items]) => (
              <section key={bundleId}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-[#004D40]" /> {t('myAddons.fromBundle')}
                </h2>
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 divide-y dark:divide-gray-700 overflow-hidden">
                  {items.map(renderRow)}
                </div>
              </section>
            ))}

            {/* Standalone add-ons */}
            {standalone.length > 0 && (
              <section>
                {bundleGroups.size > 0 && (
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    {t('myAddons.standalone')}
                  </h2>
                )}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 divide-y dark:divide-gray-700 overflow-hidden">
                  {standalone.map(renderRow)}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <Footer />

      {/* Property picker modal */}
      {applying &&
        createPortal(
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="absolute inset-0 cursor-pointer" aria-hidden="true" onClick={closePicker} />
            <div role="dialog" aria-modal="true" className="relative z-[1] w-full max-w-lg overflow-hidden rounded-xl bg-white dark:bg-gray-800 shadow-2xl">
              <div className="border-b dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] dark:text-white">
                  {t('myAddons.pickPropertyTitle')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('myAddons.pickPropertySubtitle')} · {localizedName(applying.addon)}
                </p>
              </div>

              <div className="p-4 max-h-[55vh] overflow-y-auto">
                {propsLoading ? (
                  <div className="py-10 flex justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-[#004D40]" />
                  </div>
                ) : properties.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-gray-500 mb-4">{t('myAddons.noApplicableProperties')}</p>
                    <Link to={createListingHref}>
                      <Button variant="outline" size="sm">{t('myAddons.createListing')}</Button>
                    </Link>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {properties.map((property) => (
                      <li key={property.id}>
                        <button
                          type="button"
                          disabled={applyPending}
                          onClick={() => handleApply(property.id)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-[#004D40] hover:bg-[var(--pv-color-surface-sunken)] dark:hover:bg-[#004D40]/10 transition-colors text-start disabled:opacity-50 cursor-pointer"
                        >
                          {property.images?.[0]?.url ? (
                            <img src={property.images[0].url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                              <Package className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">{propertyName(property)}</p>
                            {property.city && (
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {isRTL ? property.city.nameAr : property.city.nameEn}
                              </p>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-end gap-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-6 py-4">
                <button
                  onClick={closePicker}
                  disabled={applyPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 cursor-pointer"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
